/* eslint-disable react/jsx-indent */
/* eslint-disable react/jsx-props-no-spreading */
import { useTranslate } from 'hooks/useTranslate';

import Page from 'modules/_shared/components/Pages/Page';
import CardKPI from 'modules/_shared/components/Cards/CardKPI';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import PageListHeader from 'modules/_shared/components/Headers/PageListHeader';
import CustomDataTable from 'modules/_shared/components/Tables/CustomDataTable';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';
import EmptyStateCard from 'modules/_shared/components/EmptyStates/EmptyStateCard';

import { useEmployeeList } from './hooks/useEmployeeList';

function EmployeeList() {
  const { t } = useTranslate();

  const {
    data: {
      actualSociety,
      tableData,
      currentTableColumns,
      isAdmin,
      isReadOnly,
      isSuperAdmin,
      hasFactorialIntegration,
      pageDescription,
      activeEmployeesWithIncentive,
      inactiveEmployeesWithIncentive,
      activeEmployeesCount,
      totalEmployeesCount,
      kpiFilter,
      pagination,
    },
    actions: {
      handleLoadEmployees,
      handleFilterEmployees,
      handleSyncEmployees,
      handleKpiFilterClick,
      handleChangePage,
      handleChangeRowsPerPage,
      handleSearchChange,
      handleSort,
      fetchEmployeesPaginated,
    },
    isLoading,
  } = useEmployeeList();

  return (
    <Page id="employees-list-page">
      <PageListHeader
        subTitle={t('Employees')}
        description={pageDescription}
        actions={
          isAdmin
            ? [
                <Button
                  key="sync-employees"
                  onClick={() => handleSyncEmployees(fetchEmployeesPaginated)}
                  variant={ButtonVariants.LIGHT}
                  outlineStyle
                  disabled={!hasFactorialIntegration}
                >
                  <em className="icon ni ni-reload" />
                  <span>{t('Sync')}</span>
                </Button>,
                <Button
                  key="filter-employees"
                  disabled={isReadOnly || !hasFactorialIntegration}
                  variant={ButtonVariants.LIGHT}
                  onClick={handleFilterEmployees}
                  outlineStyle
                >
                  <em className="icon ni ni-filter" />
                  <span>{t('FilterEmployees')}</span>
                </Button>,
                <Button
                  key="update-employees"
                  disabled={!isSuperAdmin || !hasFactorialIntegration}
                  onClick={handleLoadEmployees}
                >
                  <em className="icon ni ni-download-cloud" />
                  <span>{t('UpdateEmployees')}</span>
                </Button>,
              ]
            : []
        }
      />

      {hasFactorialIntegration ? (
        <>
          <div className="nk-block row pb-4 g-4">
            <div className="col-12 col-md-4">
              <CardKPI
                title={t('Employees')}
                value={activeEmployeesCount}
                valueExtra={`${t('of')} ${totalEmployeesCount}`}
                onClick={() => handleKpiFilterClick('active')}
                isActive={kpiFilter === 'active'}
              />
            </div>
            <div className="col-12 col-md-4">
              <CardKPI
                title={t('EmployeesWithIncentives')}
                value={activeEmployeesWithIncentive}
                valueExtra={`${t('of')} ${activeEmployeesCount}`}
                onClick={() => handleKpiFilterClick('activeWithIncentives')}
                isActive={kpiFilter === 'activeWithIncentives'}
              />
            </div>
            <div className="col-12 col-md-4">
              <CardKPI
                title={t('FormerEmployeesWithIncentives')}
                value={inactiveEmployeesWithIncentive}
                valueExtra={`${t('of')} ${
                  totalEmployeesCount - activeEmployeesCount
                }`}
                onClick={() => handleKpiFilterClick('inactiveWithIncentives')}
                isActive={kpiFilter === 'inactiveWithIncentives'}
              />
            </div>
          </div>

          <CustomDataTable
            data={tableData}
            columns={currentTableColumns}
            searchBy={['externalIdSearch', t('Name'), t('Email'), t('TaxId')]}
            searchByPlaceholder={[
              t('id'),
              t('name'),
              t('email'),
              t('cif'),
            ].join()}
            pagination
            hasCustomPagination
            customPagination={{
              className: 'bg-white',
              totalItems: pagination.totalRows,
              itemPerPage: pagination.items,
              currentPage: pagination.page,
              customItemPerPage: pagination.items,
              onChangePage: handleChangePage,
              onChangeRowsPerPage: handleChangeRowsPerPage,
            }}
            sortServer
            onSort={handleSort}
            onFilterChange={handleSearchChange}
            className="nk-tb-list"
            actions
            showDense
            fileName={`${t('EmployeesList')} ${actualSociety?.name}`}
            isLoading={isLoading}
          />
        </>
      ) : (
        <EmptyStateCard
          mainMessage={t('EmployeesFactorialIntegrationMainMessage')}
          infoMessage={t('EmployeesFactorialIntegrationRequired')}
        />
      )}

      {(isLoading || !actualSociety) && <CustomLoading />}
    </Page>
  );
}

export default EmployeeList;
